// ------------------------------------------------------------------ //
// Central mock switchboard. Every mockable data source in the app is
// toggled from this file — flip a flag, reload, done. The generators
// themselves live next to their hooks in *.mock.ts files.
//
// Each flag must stay a standalone `import.meta.env.DEV && <bool>`
// const: production builds fold that to `false` and tree-shake the
// mock branch it guards. Grouping the flags into an object would
// defeat the folding (property reads aren't statically evaluated), so
// keep them as individual exports.
// ------------------------------------------------------------------ //

/** Profile › author analytics tab: fabricated sales on real quizzes. */
export const MOCK_AUTHOR_ANALYTICS = import.meta.env.DEV && false;

/**
 * Author whose profile the analytics tab impersonates while
 * MOCK_AUTHOR_ANALYTICS is on. Seeded dev authors: alen, nogac,
 * quizzach, davor, putoholicari, luka-bumbak, vragolasti-tenis,
 * filip. Empty string keeps the logged-in author's own quizzes.
 */
export const MOCK_AUTHOR_SLUG = 'davor';

/**
 * Quiz play stage (/quiz/:uuid, any uuid while mocking): a fabricated
 * standard-question quiz so the play screens render without a
 * logged-in session or purchased quiz.
 */
export const MOCK_QUIZ_PLAY = import.meta.env.DEV && false;

/**
 * Which quiz shape MOCK_QUIZ_PLAY serves: 'quick' (no premade meta),
 * 'premade' (author + answers template), 'puzzle' or 'chapters' (both
 * disable the QAQA mode on the mode-selection screen), 'kids'
 * (premade with for_kids — the play stage swaps every font to DynaPuff),
 * 'patterns' (all-standard premade exercising every content-pattern
 * layout — lyrics, citat, godina, podpitanja, anagram, priča, recept,
 * rukopisni popis, poredak, što povezuje, napomena, kick).
 */
export const MOCK_QUIZ_PLAY_VARIANT: import('./quizPlay.mock').QuizPlayMockVariant =
	'patterns';

/**
 * Admin question/answer preview (/preview/question/:id and
 * /preview/answer/:id): serves a question from the MOCK_QUIZ_PLAY quiz
 * (same MOCK_QUIZ_PLAY_VARIANT) instead of the backend — a numeric :id
 * picks that question (1-based), anything else falls back to the first.
 */
export const MOCK_QUESTION_PREVIEW = import.meta.env.DEV && false;

/**
 * Cart summary (/generator/summary): a fabricated cart with a generator
 * item covering both configurator modes (whole-category pick and
 * per-subcategory pick), plus premade and quick items. The route is
 * auth-guarded client-side — put any JWT with a future `exp` into
 * localStorage as 'userToken' to pass it.
 */
export const MOCK_CART = import.meta.env.DEV && false;

/**
 * TV pairing screen (/quiz/:uuid/play): a fabricated 4-digit code with
 * a short expiry, so the digits, the refresh cycle and the loading
 * dots render without a backend session. Pair with MOCK_QUIZ_PLAY to
 * reach the screen without logging in.
 */
export const MOCK_TV_CODE = import.meta.env.DEV && false;

/** Profile › transactions tab: fabricated order history. */
export const MOCK_USER_ORDERS = import.meta.env.DEV && false;

/**
 * Order finish screen (/order/:uuid, any uuid while mocking): a
 * fabricated multi-quiz order — two real premades plus a quick and a
 * generator item, so the plural title and every row variant render.
 */
export const MOCK_ORDER = import.meta.env.DEV && false;
